// The plan panel — the agent's declared plan as a checklist that fills in, with one
// row per agent: the workflow agent, then a row per module.
//
// Presentational. All the rules live in the pure `agentActivity.js`; this file
// only picks a glyph and lays a row out. It imports NO api module and NO stylesheet
// (the `.dash-*` rules live in `Dashboard.css`, which `Dashboard.js` imports —
// `DashboardSidebar` and `DashboardComposer` are arranged the same way), so it is
// cheap to render under jest.
//
// It sits ABOVE the composer and BELOW the transcript rather than interleaved into
// the messages, for two reasons: a row MUTATES (pending → working → done, or
// working → needs_input → working), so interleaving would mean either a duplicate
// row per transition or re-ordering the transcript under the reader's eyes; and the
// whole value of a plan is that it stays visible while the transcript scrolls — one
// you have to scroll back to find is not an overview.
//
// A step that needs the user carries its Open control IN ITS OWN ROW, so "which
// module is blocked" and "go unblock it" are one glance and one click.

import { useState } from 'react';
import {
  activityRows,
  activityStateClass,
  planHeader,
  planProgress,
  CANCELLED,
  DONE,
  FAILED,
  NEEDS_INPUT,
  PAUSED,
  PENDING,
} from './agentActivity';
import { MODE_AUTO, MODE_MANUAL, modeLabel } from '../panels/chatModes';
import {
  AlertCircleIcon,
  AlertTriangleIcon,
  CheckCircleIcon,
  CircleDashedIcon,
  CirclePauseIcon,
  CircleSlashIcon,
  SpinnerIcon,
} from '../components/icons';

// The DEFAULT is a spinner, which is why every state needs a case here: an unmapped one
// renders as ongoing work. That is exactly how a cancelled run kept a spinning module
// row — its `cancelled` state had no branch and fell through to this.
function RowIcon({ state }) {
  if (state === DONE) return <CheckCircleIcon className="dash-activity-icon" />;
  if (state === NEEDS_INPUT) return <AlertCircleIcon className="dash-activity-icon" />;
  if (state === FAILED) return <AlertTriangleIcon className="dash-activity-icon" />;
  if (state === CANCELLED) return <CircleSlashIcon className="dash-activity-icon" />;
  if (state === PAUSED) return <CirclePauseIcon className="dash-activity-icon" />;
  if (state === PENDING) return <CircleDashedIcon className="dash-activity-icon" />;
  return <SpinnerIcon className="dash-activity-icon dash-activity-spin" />;
}

export default function DashboardActivity({
  run,
  onOpenTile,
  // PLAN mode: the declared plan is the thing awaiting a decision, so the approve
  // control goes HERE rather than in a band of its own — the plan is already
  // rendered on this panel, and a band would be a second copy of the same list
  // drifting from it. Both gates are required: the mode (the caller's) and a plan
  // with steps (checked below), so nothing can be approved before the agent has
  // actually proposed something.
  planAwaitingApproval = false,
  // `(mode) => Promise` — approve and start the build in that permission mode.
  onApprovePlan,
  // `(document) => Promise` — save the plan the user edited. Absent hides the editor,
  // which is what keeps this component renderable with no host wiring.
  onSavePlan,
  // Which approve button reads as primary. Presentation only: both are always
  // clickable, because the point of offering two is that the choice is made HERE.
  preferredMode = MODE_AUTO,
}) {
  const [open, setOpen] = useState(true);
  // WHICH mode is being approved, not just "an approval is in flight": both buttons
  // disable, but only the one that was clicked says "Starting…". Two buttons reporting
  // that at once would leave the user unsure which mode the build is starting in —
  // which is the single thing the pair exists to make explicit.
  const [approvingMode, setApprovingMode] = useState(null);
  // The plan editor. `draft` is null when closed — distinct from `''`, which is a
  // user who has cleared the whole document and will be told so by the parser.
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [planErrors, setPlanErrors] = useState(null);
  const rows = activityRows(run);
  if (!rows.length) return null;

  const header = planHeader(run);
  const { done, total } = planProgress(rows);
  const canApprovePlan = planAwaitingApproval && !!onApprovePlan && !!run?.plan?.steps?.length;
  const document = run?.plan?.document || '';
  const canEditPlan = !!onSavePlan && !!document;
  const approving = approvingMode !== null;
  const busy = saving || approving;

  async function savePlan() {
    if (saving) return;
    setSaving(true);
    setPlanErrors(null);
    try {
      await onSavePlan(draft);
      // Closed on success so the panel returns to the checklist the user came for,
      // and re-seeded from the server's own re-serialisation next time it is opened —
      // never from the draft, or a value the parser normalised would silently differ
      // from what is stored.
      setDraft(null);
    } catch (err) {
      // The backend answers a bad edit with `{message, errors:[{line, message}]}`, and
      // NOTHING was written, so the draft stays exactly as typed.
      const detail = err?.detail;
      setPlanErrors({
        message: detail?.message || err?.message || String(err),
        errors: detail?.errors || [],
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="dash-activity">
      <button
        type="button"
        className="dash-activity-head"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        {/* "Plan" once one is declared, "Agents" for a run that never planned —
            calling an unplanned list a plan would be a lie about what it is. */}
        <span className="dash-activity-title">{header ? 'Plan' : 'Agents'}</span>
        {/* The matched pipeline: the agent's answer to "what did you think I was
            asking for". Its reasoning is the `why` line below. */}
        {header && <span className="dash-activity-pipeline">{header.pipeline}</span>}
        <span className="dash-activity-count">
          {total > 0 ? `${done}/${total}` : 'no modules yet'}
        </span>
        <span className="dash-activity-toggle" aria-hidden="true">
          {open ? '▾' : '▸'}
        </span>
      </button>

      {open && (
        <>
          {header?.why && <p className="dash-activity-why">{header.why}</p>}
          <ul className="dash-activity-list">
            {rows.map((row) => (
              <li key={row.key} className={`dash-activity-row ${activityStateClass(row.state)}`}>
                <RowIcon state={row.state} />
                <span className="dash-activity-label">{row.label}</span>
                {/* A step the user reserved for themselves. It is still created,
                    wired and activated — it PARKS for them instead of running a
                    subagent — so the chip says whose it is, not that it is skipped. */}
                {row.owner === 'user' && (
                  <span className="dash-activity-owner" title="You are doing this step">
                    you
                  </span>
                )}
                {/* `attention` marks a module whose last JOB failed or was cancelled.
                    The row stays `working` on purpose — its subagent is about to
                    diagnose and may retry — but a failure captioned like quiet
                    progress reads as "busy" rather than "broken", so the caption goes
                    amber while the spinner stays. */}
                <span
                  className={`dash-activity-detail${
                    row.attention ? ' dash-activity-detail--attention' : ''
                  }`}
                >
                  {row.detail}
                </span>
                {/* On a step not yet started, `detail` is the ASSIGNMENT (what that
                    subagent will be asked to do), so the outcome rides alongside —
                    "who does what" and "what I get" are different questions and a
                    plan should answer both. Once the module reports for itself,
                    `detail` becomes what actually happened and this is dropped. */}
                {row.state === PENDING && row.produces && row.does && (
                  <span className="dash-activity-produces">→ {row.produces}</span>
                )}
                {/* Only a module the user must act on gets a control, and only when
                    it is a real tile — the workflow agent's row carries a null
                    tileId because it is not a module, and a plan step not yet
                    started has no tile to open. */}
                {row.state === NEEDS_INPUT && row.tileId && onOpenTile && (
                  <button
                    type="button"
                    className="dash-activity-open"
                    onClick={() => onOpenTile(row.tileId)}
                  >
                    Open →
                  </button>
                )}
              </li>
            ))}
          </ul>

          {/* Approving is what STARTS the work, so it says so rather than just
              "Approve" — in plan mode nothing has been built yet, and the button is
              the transition out of that. There is no "Keep planning" counterpart
              (ChatPanel has one only because it clears its own input): saying what
              to change instead is just the next message, which the composer below
              already is. */}
          {/* EDIT the plan before approving it. A document rather than a form of
              typed fields, because the correction that matters is a substitution —
              "not steel, aluminium", "that face not this one", "owner: user" — and
              one line changed is one thing changed. Server-parsed, so the errors
              below carry line numbers and a bad edit writes nothing. */}
          {canEditPlan && (
            <div className="dash-plan-edit">
              {draft === null ? (
                <button
                  type="button"
                  className="dash-plan-edit-toggle"
                  onClick={() => {
                    setDraft(document);
                    setPlanErrors(null);
                  }}
                >
                  ✎ Edit plan
                </button>
              ) : (
                <>
                  <textarea
                    className="dash-plan-doc"
                    value={draft}
                    spellCheck={false}
                    disabled={saving}
                    onChange={(e) => setDraft(e.target.value)}
                    aria-label="The plan, as an editable document"
                  />
                  <p className="dash-plan-edit-hint">
                    Change a value to substitute it. <code># assumed</code> marks something I chose
                    rather than was told — editing the value clears it. Set <code>owner: user</code>{' '}
                    on a step to do that module yourself.
                  </p>
                  {planErrors && (
                    <div className="dash-plan-errors">
                      <p className="dash-plan-errors-message">{planErrors.message}</p>
                      {planErrors.errors.length > 0 && (
                        <ul>
                          {planErrors.errors.map((e, i) => (
                            <li key={`${e.line}-${i}`}>
                              {e.line ? `Line ${e.line}: ` : ''}
                              {e.message}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                  <div className="dash-plan-edit-actions">
                    <button
                      type="button"
                      className="dash-plan-save-btn"
                      disabled={saving}
                      onClick={savePlan}
                    >
                      {saving ? 'Saving…' : 'Save plan'}
                    </button>
                    <button
                      type="button"
                      className="dash-plan-cancel-btn"
                      disabled={saving}
                      onClick={() => {
                        setDraft(null);
                        setPlanErrors(null);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Approving is what STARTS the work, so it says so rather than just
              "Approve" — in plan mode nothing has been built yet, and the button is
              the transition out of that. TWO buttons, because the mode the build runs
              in is a real choice and guessing it from whatever the chip happened to
              say before Plan was selected was exactly that: a guess. There is no
              "Keep planning" counterpart (ChatPanel has one only because it clears
              its own input): saying what to change instead is just the next message,
              which the composer below already is. */}
          {canApprovePlan && (
            <div className="dash-activity-approve">
              {[MODE_AUTO, MODE_MANUAL].map((mode) => {
                const primary = mode === (preferredMode === MODE_MANUAL ? MODE_MANUAL : MODE_AUTO);
                return (
                  <button
                    key={mode}
                    type="button"
                    className={`dash-plan-approve-btn${primary ? '' : ' dash-plan-approve-btn--alt'}`}
                    disabled={busy}
                    title={
                      mode === MODE_AUTO
                        ? 'Build the whole pipeline without stopping to ask'
                        : 'Build it, but ask me before each change'
                    }
                    onClick={async () => {
                      setApprovingMode(mode);
                      try {
                        await onApprovePlan(mode);
                      } catch {
                        // SWALLOWED, and only here: reporting is the host's job (it
                        // owns the error banner, and `Dashboard.handleApprovePlan`
                        // already catches). This exists so the component does not
                        // depend on that — a host that let the rejection through
                        // would otherwise leave an unhandled promise and, worse, skip
                        // the re-enable below.
                      } finally {
                        // The panel is not unmounted by approving (the plan stays on
                        // the record and the rows keep filling in), so the buttons
                        // have to be re-enabled or a FAILED approval could never be
                        // retried.
                        setApprovingMode(null);
                      }
                    }}
                  >
                    {approvingMode === mode
                      ? 'Starting…'
                      : `✓ Approve & build — ${modeLabel(mode).toLowerCase()}`}
                  </button>
                );
              })}
              <span className="dash-activity-approve-hint">
                Nothing has been built yet — approving starts the work.
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
